import { z } from 'zod';

import zodLocales from 'common/zod/locales';
import { isValidEmail } from 'components/LegacyInput/utils';

import locales from './locales';

export const MAX_VALIDITY_PERIOD = 99;

/**
 * invoiceSettingsId
 */
export const invoiceSettingsIdFormSchema = z.string();

/**
 * quoteValidityPeriod
 */
export const quoteValidityPeriodSchema = z.coerce
  .number()
  .positive({ message: zodLocales.errors.positive })
  .int({ message: zodLocales.errors.integer })
  .max(MAX_VALIDITY_PERIOD, {
    message: zodLocales.formatString(
      zodLocales.errors.maxValue,
      MAX_VALIDITY_PERIOD,
    ) as string,
  })
  .nullable();

/**
 * issuerEmail
 */
export const issuerEmailRequiredFormSchema = z.string().trim();
export const issuerEmailNullableFormSchema =
  issuerEmailRequiredFormSchema.nullable();

/**
 * lateMaxDays
 */
export const lateMaxDaysFormSchema = z
  .number()
  .min(0, { message: zodLocales.errors.positive })
  .int({ message: zodLocales.errors.integer });

/**
 * legalMentions
 */
export const legalMentionsRequiredFormSchema = z.string().trim();

export const legalMentionsArrayFormSchema = z.string().trim().array();

/**
 * lateFeePercentage
 */
export const getLateFeePercentageFormSchema = (minLatePaymentFee: number) =>
  z
    .number({
      invalid_type_error: zodLocales.errors.required,
      required_error: zodLocales.errors.required,
    })
    .step(0.01, {
      message: zodLocales.formatString(
        zodLocales.errors.maxDecimals,
        2,
      ) as string,
    })
    .min(minLatePaymentFee, {
      message: locales.formatString(locales.errors.lateFeeTooLow, {
        min: minLatePaymentFee,
      }) as string,
    });

/**
 * displayTradeName
 */
export const displayTradeNameBooleanFormSchema = z.boolean();

interface OptionsFormSchemaParameters {
  minLatePaymentFee: number;
}

export const getOptionsFormSchema = ({
  minLatePaymentFee,
}: OptionsFormSchemaParameters) =>
  z.object({
    issuerEmail: issuerEmailNullableFormSchema.refine(
      (issuerEmail) => (issuerEmail ? isValidEmail(issuerEmail) : true),
      { message: locales.errors.invalidIssuerEmail },
    ),
    lateMaxDays: lateMaxDaysFormSchema,
    legalMentions: legalMentionsArrayFormSchema,
    paymentLateFee: getLateFeePercentageFormSchema(minLatePaymentFee),
  });

export type OptionsFormSchema = z.infer<
  ReturnType<typeof getOptionsFormSchema>
>;
